import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  BarChart3, Users, Package2, FolderKanban, Store, Sparkles, FileText,
  Heart, TrendingUp, TrendingDown, Eye, ShoppingCart, Calendar, Loader2,
  ArrowLeft, RefreshCw, DollarSign, MessageCircle, Mail, BookOpen, Languages, Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, className = "" }) => (
  <div className={`rounded-2xl bg-white border border-stone-100 p-6 shadow-sm ${className}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
        {subtitle && (
          <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        )}
        {trend && (
          <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className="rounded-xl bg-primary/10 p-3">
        <Icon className="h-6 w-6 text-primary" />
      </div>
    </div>
  </div>
);

// Section header component
const SectionHeader = ({ icon: Icon, title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-primary" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
    {children}
  </div>
);

// Activity chart bar
const ActivityBar = ({ label, value, maxValue, color = "bg-primary" }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-8">{label}</span>
      <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
      <span className="text-sm font-medium w-8 text-right">{value}</span>
    </div>
  );
};

export default function AdminAnalytics() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(response.data);
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load analytics:', error);
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
            <p className="text-muted-foreground mb-6">You don&apos;t have permission to view this page.</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate max values for charts
  const maxActivity = Math.max(
    ...analytics.activity_timeline.map(d => d.users + d.yarns + d.projects),
    1
  );
  const maxYarnWeight = Math.max(...analytics.inventory.yarn_weights.map(w => w.count), 1);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-extrabold tracking-tight">
              Analytics <span className="text-primary">Dashboard</span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Site-wide metrics and engagement statistics
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              className="rounded-full"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/admin/users')} 
              variant="outline" 
              className="rounded-full"
            >
              <Users className="h-4 w-4 mr-2" />
              Käyttäjät
            </Button>
            <Button 
              onClick={() => navigate('/admin/translations')} 
              variant="outline" 
              className="rounded-full"
            >
              <Languages className="h-4 w-4 mr-2" />
              Tekstit
            </Button>
            <Button 
              onClick={() => navigate('/admin/legal')} 
              variant="outline" 
              className="rounded-full"
            >
              <Scale className="h-4 w-4 mr-2" />
              Legal-sivut
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Users"
            value={analytics.users.total.toLocaleString()}
            subtitle={`${analytics.users.this_week} this week`}
            icon={Users}
            trend={analytics.users.this_week > 0 ? "up" : undefined}
            trendValue={analytics.users.this_week > 0 ? `+${analytics.users.this_week} new` : undefined}
          />
          <StatCard
            title="Yarn Skeins"
            value={analytics.inventory.total_yarns.toLocaleString()}
            subtitle={`${analytics.inventory.yarns_this_week} added this week`}
            icon={Package2}
          />
          <StatCard
            title="Projects"
            value={analytics.inventory.total_projects.toLocaleString()}
            subtitle={`${analytics.inventory.projects_by_status.in_progress} in progress`}
            icon={FolderKanban}
          />
          <StatCard
            title="Blog Views"
            value={analytics.blog.total_views.toLocaleString()}
            subtitle={`${analytics.blog.published} published posts`}
            icon={Eye}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Activity Timeline */}
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm">
            <SectionHeader icon={Calendar} title="7-Day Activity" />
            <div className="space-y-3">
              {analytics.activity_timeline.map((day) => (
                <div key={day.date} className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{day.day} ({day.date.slice(5)})</span>
                    <span>{day.users + day.yarns + day.projects} total</span>
                  </div>
                  <div className="flex gap-1 h-4">
                    <div 
                      className="bg-blue-500 rounded-sm transition-all"
                      style={{ width: `${(day.users / maxActivity) * 100}%`, minWidth: day.users > 0 ? '4px' : '0' }}
                      title={`${day.users} users`}
                    />
                    <div 
                      className="bg-primary rounded-sm transition-all"
                      style={{ width: `${(day.yarns / maxActivity) * 100}%`, minWidth: day.yarns > 0 ? '4px' : '0' }}
                      title={`${day.yarns} yarns`}
                    />
                    <div 
                      className="bg-green-500 rounded-sm transition-all"
                      style={{ width: `${(day.projects / maxActivity) * 100}%`, minWidth: day.projects > 0 ? '4px' : '0' }}
                      title={`${day.projects} projects`}
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 mt-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-sm" />
                  <span>Users</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-primary rounded-sm" />
                  <span>Yarns</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-sm" />
                  <span>Projects</span>
                </div>
              </div>
            </div>
          </div>

          {/* Yarn Weight Distribution */}
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm">
            <SectionHeader icon={Package2} title="Yarn Weight Distribution" />
            <div className="space-y-2">
              {analytics.inventory.yarn_weights.slice(0, 8).map((item) => (
                <ActivityBar
                  key={item.weight}
                  label={item.weight.slice(0, 6)}
                  value={item.count}
                  maxValue={maxYarnWeight}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Revenue & Shop Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Design Sales"
            value={analytics.designs.purchases.total}
            subtitle={`${analytics.designs.purchases.this_week} this week`}
            icon={ShoppingCart}
          />
          <StatCard
            title="Design Revenue"
            value={`€${analytics.designs.revenue.toFixed(2)}`}
            subtitle={`${analytics.designs.paid} paid designs`}
            icon={DollarSign}
          />
          <StatCard
            title="Donations"
            value={`€${analytics.donations.revenue.toFixed(2)}`}
            subtitle={`${analytics.donations.total} supporters`}
            icon={Heart}
          />
          <StatCard
            title="Subscribers"
            value={analytics.blog.subscribers.total}
            subtitle={`${analytics.blog.subscribers.this_week} this week`}
            icon={Mail}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Designs Stats */}
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm">
            <SectionHeader icon={Sparkles} title="Crochet Designs">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/designs')}
                className="text-primary"
              >
                Manage →
              </Button>
            </SectionHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Published</span>
                <span className="font-semibold">{analytics.designs.published}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Free Patterns</span>
                <span className="font-semibold text-green-600">{analytics.designs.free}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Paid Patterns</span>
                <span className="font-semibold text-primary">{analytics.designs.paid}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Total Purchases</span>
                <span className="font-semibold">{analytics.designs.purchases.total}</span>
              </div>
            </div>
          </div>

          {/* Blog Stats */}
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm">
            <SectionHeader icon={FileText} title="Blog & News">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/blog')}
                className="text-primary"
              >
                Manage →
              </Button>
            </SectionHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Published Posts</span>
                <span className="font-semibold">{analytics.blog.published}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Total Views</span>
                <span className="font-semibold">{analytics.blog.total_views.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Comments</span>
                <span className="font-semibold">{analytics.blog.comments.total}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Pending Approval</span>
                <span className={`font-semibold ${analytics.blog.comments.pending > 0 ? 'text-orange-500' : ''}`}>
                  {analytics.blog.comments.pending}
                </span>
              </div>
            </div>
          </div>

          {/* Projects Stats */}
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm">
            <SectionHeader icon={FolderKanban} title="User Projects" />
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground">Total Projects</span>
                <span className="font-semibold">{analytics.inventory.total_projects}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  In Progress
                </span>
                <span className="font-semibold">{analytics.inventory.projects_by_status.in_progress}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-muted-foreground flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  Completed
                </span>
                <span className="font-semibold">{analytics.inventory.projects_by_status.completed}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground flex items-center gap-2">
                  <div className="w-2 h-2 bg-stone-400 rounded-full" />
                  Planned
                </span>
                <span className="font-semibold">{analytics.inventory.projects_by_status.planned}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Selling Designs */}
        {analytics.designs.top_selling.length > 0 && (
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm mb-8">
            <SectionHeader icon={TrendingUp} title="Top Selling Designs" />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b border-stone-100">
                    <th className="pb-3 font-medium">#</th>
                    <th className="pb-3 font-medium">Design</th>
                    <th className="pb-3 font-medium text-right">Sales</th>
                    <th className="pb-3 font-medium text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.designs.top_selling.map((design, index) => (
                    <tr key={design._id} className="border-b border-stone-50 last:border-0">
                      <td className="py-3 text-muted-foreground">{index + 1}</td>
                      <td className="py-3 font-medium">{design.title}</td>
                      <td className="py-3 text-right">{design.sales}</td>
                      <td className="py-3 text-right font-semibold">€{design.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Community Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Marketplace Listings"
            value={analytics.marketplace.total_listings}
            subtitle={`${analytics.marketplace.listings_this_week} this week`}
            icon={Store}
          />
          <StatCard
            title="Shared Patterns"
            value={analytics.patterns.public}
            subtitle={`of ${analytics.patterns.total} total`}
            icon={BookOpen}
          />
          <StatCard
            title="Active Stash Users"
            value={analytics.users.with_stash}
            subtitle={`of ${analytics.users.total} users`}
            icon={Users}
          />
          <StatCard
            title="New This Month"
            value={analytics.users.this_month}
            subtitle="user registrations"
            icon={TrendingUp}
            trend={analytics.users.this_month > 0 ? "up" : undefined}
          />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          Last updated: {new Date(analytics.generated_at).toLocaleString()}
        </div>
      </main>
    </div>
  );
}
